// Updated ActionPage component with fixes for sales_type and item_id issues
import React, { useState, useEffect } from "react";
import classes from './ActionPage.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { Trash2 } from "lucide-react";
import api, { BASE_URL, withCompanyId } from "../../Components/API/Api";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useUser } from "../../UserContext";
import AddCustomerModal from "../../Components/Modals/AddCustomerModal";
import { normalizeAutoVatSetting, shouldDisableVatCheckbox } from "../Settings/organizationSettingsUtils";
import { normalizeInvoiceRows, resolveInvoiceReference, resolveInvoiceTotals } from "./actionPageUtils";

const ActionPage = () => {
  const { user } = useUser();
  const userRole = (user?.role || user?.user_type || user?.account_type || user?.type || '').toLowerCase();
  const isSuperAdmin = userRole === 'super admin' || userRole === 'superadmin' || userRole === 'super_admin';
  const location = useLocation();
  const editInvoice = location.state?.invoice;
  const isEditMode = Boolean(editInvoice);
  // Fix 1: Ensure sales_type matches API expectations
  const [salesType, setSalesType] = useState("credit");
  const [currency, setCurrency] = useState("");
  const [showPrint, setShowPrint] = useState(false);
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [quantityErrors, setQuantityErrors] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [responseData, setResponseData] = useState(null);
  const [services, setServices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const navigate = useNavigate();
  const [vatRates, setVatRates] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [autoVatDeduction, setAutoVatDeduction] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  // Fix 2: Ensure type matches sales_type format
  const [type, setType] = useState("product"); // Changed from "products" to "product"
  const [transactionDate, setTransactionDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [focusedDescriptionIndex, setFocusedDescriptionIndex] = useState(null);

  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
  const [newCustomerInitialName, setNewCustomerInitialName] = useState("");

  const handleCustomerAdded = (newCustomer) => {
    setCustomers((prev) => [...prev, newCustomer]);

    // Automatically select the new customer
    setSelectedCustomerName(newCustomer.name);
    setSelectedCustomerId(newCustomer.id);
    setCustomerInputValue("");
  };

  const formatNumberInput = (value) => {
    const raw = value.replace(/,/g, '');
    if (!raw) return '';
    const number = parseFloat(raw);
    if (isNaN(number)) return '';
    return number.toLocaleString();
  };

  const cleanNumber = (value) => {
    const num = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const [bearer, setBearer] = useState('');

  // Reset all form data
  const resetFormData = () => {
    setSelectedCustomerName("");
    setSelectedCustomerId("");
    setCustomerInputValue("");
    setTransactionDate("");
    setDueDate("");
    setSelectedCurrency(null);
    setCurrency("");
    setSelectedStoreId("");
    setInvoiceItems([
      {
        type: type === "product" ? "product" : "service", // Fixed to match singular form
        ledger: "",
        description: "",
        qty: 1,
        price: 0,
        discountRate: "",
        discountAmount: "",
        discount: 0,
        vatRate: "Premium",
        vat: 0,
        vatChecked: true,
        tax_id: 1,
        wht: 0,
        total: 0,
        item_id: null // Fix 3: Ensure item_id is explicitly set to null initially
      }
    ]);
    setResponseData(null);
    setShowPrint(false);
  };

  const readData = async () => {
    try {
      const value = localStorage.getItem('authToken');
      if (value !== null) {
        setBearer(value);
        console.log(value);
      }
    } catch (e) {
      alert('Failed to fetch the input from storage');
    }
  };

  useEffect(() => {
    readData();
  }, []);

  // Fetch both products and services
  useEffect(() => {
    const fetchItems = async () => {
      const token = localStorage.getItem("authToken");

      try {
        const [servicesRes, stocksRes, productsRes] = await Promise.all([
          api.get(`${BASE_URL}/v1/items/fetch-services`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
          }),
          api.get(`${BASE_URL}/v1/fetch_stocks`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
          }),
          api.get(`${BASE_URL}/v1/items/fetch-products`, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
          })
        ]);

        if (servicesRes?.data?.status) setServices(servicesRes.data.data || []);
        if (stocksRes?.data?.status) setStocks(stocksRes.data.data || []);
        if (productsRes?.data?.status) setProducts(productsRes.data.data || []);

      } catch (error) {
        console.error("Error fetching services/stocks/products:", error);
      }
    };

    fetchItems();
  }, []);

  // Fetch chart of accounts for Services tab (use gl_name as display name)
  useEffect(() => {
    const fetchAccounts = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      try {
        const res = await api.get(`${BASE_URL}/v1/get-income-accounts`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const payload = res?.data;
        let raw = payload?.data ?? payload;
        if (raw && typeof raw === "object" && Array.isArray(raw.data)) {
          raw = raw.data;
        }
        const list = Array.isArray(raw) ? raw : [];
        // Normalize so the Select + handlers can use .name while
        // still relying on the backend's gl_name field.
        const normalized = list.map((acc) => ({
          ...acc,
          id: acc.id,
          name: acc.gl_name ?? acc.name ?? acc.account_name ?? "",
          description: acc.description ?? "",
        }));
        setAccounts(normalized);
      } catch (err) {
        console.error("Error fetching accounts for services tab:", err);
        setAccounts([]);
      }
    };

    fetchAccounts();
  }, []);

  // Fetch stores for Super Admins
  useEffect(() => {
    const fetchStores = async () => {
      if (!isSuperAdmin) return;
      setLoadingStores(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get(`${BASE_URL}/v1/store`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
        });
        let storesData = res.data?.data?.data || res.data?.data || res.data;
        if (!Array.isArray(storesData)) storesData = [];
        const normalizedStores = storesData.map(s => ({ id: s.id ?? s.store_id ?? s.value, name: s.name ?? s.store_name ?? s.title ?? `Store ${s.id ?? s.store_id}` }));
        setStores(normalizedStores);
      } catch (err) {
        console.error("Error fetching stores:", err);
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, [isSuperAdmin]);

  // When a store is selected by Super Admin, fetch only that store's products and clear mismatched rows
  useEffect(() => {
    const fetchStoreProducts = async () => {
      if (!isSuperAdmin) return;
      if (!selectedStoreId) {
        setProducts([]);
        setInvoiceItems(prev => prev.map(row => {
          const cleared = { ...row, ledger: "", item_id: null, description: "", price: 0 }; // Fix: set item_id to null
          return recomputeRow(cleared, "price");
        }));
        return;
      }
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get(`${BASE_URL}/v1/get_store_products`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          params: { store_id: selectedStoreId }
        });
        const raw = res.data?.data?.data || res.data?.data || res.data;
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((p) => ({
          id: p?.id,
          name: p?.name || p?.product_name || p?.title || `Product ${p?.id || ''}`,
          price: p?.price || p?.unit_price || 0,
          quantity: p?.quantity || `Product ${p?.quantity || ''}`,
          description: p?.description || ''
        }));
        setProducts(normalized);

        const allowedIds = new Set(normalized.map(p => p.id));
        const allowedNames = new Set(normalized.map(p => p.name));
        setInvoiceItems(prev => prev.map(row => {
          const hasIdMismatch = row.item_id && !allowedIds.has(row.item_id);
          const hasNameMismatch = row.ledger && !allowedNames.has(row.ledger);
          if (hasIdMismatch || hasNameMismatch) {
            const cleared = { ...row, ledger: "", item_id: null, description: "", price: 0 }; // Fix: set item_id to null
            return recomputeRow(cleared, "price");
          }
          return row;
        }));
      } catch (err) {
        console.error("Error fetching products for store:", err);
        setProducts([]);
        setInvoiceItems(prev => prev.map(row => {
          const cleared = { ...row, ledger: "", item_id: null, description: "", price: 0 }; // Fix: set item_id to null
          return recomputeRow(cleared, "price");
        }));
      }
    };

    fetchStoreProducts();
  }, [isSuperAdmin, selectedStoreId]);

  const [items, setItems] = useState([
    { ledger: "", qty: 1, price: 0, discount: 0, discountRate: 0, vat: 0, description: "", total: 0 }
  ]);

  const [invoiceItems, setInvoiceItems] = useState([
    {
      type: "product", // Fixed to singular form
      ledger: "",
      description: "",
      userEditedDescription: false,
      qty: 1,
      price: 0,
      discountRate: "",
      discountAmount: "",
      discount: 0,
      vatRate: "Premium",
      vat: 0,
      vatChecked: false,
      tax_id: null,
      wht: 0,
      total: 0,
      item_id: null // Fix: Explicitly set item_id
    }
  ]);

  const pageMap = {
    '/dashboard': 'Dashboard',
    '/transactions': 'Transactions',
    '/accounts': 'Accounting',
    '/reports': 'Reports',
    '/banking': 'Banking',
    '/settings': 'Settings',
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      const token = localStorage.getItem("authToken");
      setLoadingCustomers(true);
      try {
        const res = await api.get(`${BASE_URL}/v1/customer`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const customerData = res.data?.data?.data || res.data?.data || [];
        setCustomers(customerData);
      } catch (err) {
        console.error("Error fetching customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  const fetchInitialData = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const [refRes, currencyRes] = await Promise.all([
        api.get(`${BASE_URL}/v1/generate-sales-invoice-code`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }),
        api.get(`${BASE_URL}/v1/fetch-all-currencies`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }),
      ]);
      if (isEditMode) {
        const existingReference = resolveInvoiceReference(editInvoice, "");
        if (existingReference) {
          setReference(existingReference);
        }
      } else if (refRes?.data?.data) {
        setReference(refRes.data.data.toString());
      }

      if (currencyRes?.data?.data) setCurrencies(currencyRes.data.data);
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }
  };

  useEffect(() => {
    if (bearer) {
      fetchInitialData();
    }
  }, [bearer]);

  useEffect(() => {
    if (!editInvoice) return;

    const customerName = editInvoice.customer?.name || editInvoice.customer_name || editInvoice.customer?.customer_name || "";
    const customerId = editInvoice.customer_id || editInvoice.customer?.id || "";
    const invoiceDate = editInvoice.invoice_date || editInvoice.transaction_date || "";
    const invoiceDueDate = editInvoice.due_date || editInvoice.dueDate || "";
    const currencyValue = editInvoice.currency?.id ?? editInvoice.currency_id ?? editInvoice.currency ?? "";
    const storeValue = editInvoice.store_id ?? editInvoice.store?.id ?? "";

    setSelectedCustomerName(customerName);
    setSelectedCustomerId(customerId);
    setCustomerInputValue("");
    setTransactionDate(String(invoiceDate).split("T")[0]);
    setDueDate(String(invoiceDueDate).split("T")[0]);
    setReference(editInvoice.invoice_number || editInvoice.reference || "");
    setDescription(editInvoice.description || "");
    setAmount(editInvoice.amount || "");
    setSelectedStoreId(storeValue ? String(storeValue) : "");

    if (currencyValue) {
      setCurrency(currencyValue);
      setSelectedCurrency({
        value: currencyValue,
        label: String(editInvoice.currency?.code || currencyValue),
      });
    }

    const normalizedRows = normalizeInvoiceRows(editInvoice);
    if (normalizedRows.length) {
      setInvoiceItems(normalizedRows);
    }
  }, [editInvoice]);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('activeCompanyId');
        if (!companyId) return;

        const res = await api.get('/v1/companies/get-info', {
          headers: { company_id: companyId },
        });

        const items = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        const company = Array.isArray(items) && items.length ? (items.find((item) => String(item.id) === String(companyId)) || items[0]) : null;
        if (!company) return;

        const normalized = normalizeAutoVatSetting(company.auto_vat_deduction);
        setAutoVatDeduction(normalized);

        if (normalized) {
          setInvoiceItems(prev => prev.map(row => ({
            ...row,
            vatChecked: true,
            tax_id: row.tax_id || 1,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch company VAT setting', err);
      }
    };

    fetchCompanySettings();
  }, []);

  // Default currency to NGN in UI and payload once currencies load
  useEffect(() => {
    if (!currency && Array.isArray(currencies) && currencies.length > 0) {
      const ngn = currencies.find(cur =>
        (cur.code && String(cur.code).toUpperCase() === 'NGN') ||
        (cur.symbol && (String(cur.symbol).includes('₦') || String(cur.symbol).toUpperCase() === 'NGN')) ||
        (cur.country && String(cur.country).toLowerCase().includes('nigeria'))
      );
      if (ngn) {
        setCurrency(ngn.id);
        setSelectedCurrency({ value: ngn.id, label: `${ngn.symbol} ${ngn.code} - ${ngn.country}`, currency: ngn });
      }
    }
  }, [currencies, currency]);

  const handleProductSelect = (rowIndex, productName) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      const selectedProduct = Array.isArray(stocks)
        ? stocks.find(p => p.name === productName)
        : undefined;

      updatedItems[rowIndex].ledger = productName;
      if (selectedProduct) {
        updatedItems[rowIndex].price = selectedProduct.price;
      }
      return updatedItems;
    });
  };

  const handleAddRow = () => {
    setInvoiceItems(prev => [
      ...prev,
      {
        type: type === "product" ? "product" : "service", // Fixed to singular form
        ledger: "",
        description: "",
        userEditedDescription: false,
        qty: 1,
        price: 0,
        discountRate: "",
        discountAmount: "",
        discount: 0,
        vatRate: "Premium",
        vat: 0,
        vatChecked: true,
        tax_id: 1,
        wht: 0,
        total: 0,
        item_id: null // Fix: Explicitly set item_id
      }
    ]);
  };

  // Handle tab switching - clear items when switching between products and services
  const handleTypeSwitch = (newType) => {
    setType(newType);

    // Clear store selection when switching to services
    if (newType === "service") {
      setSelectedStoreId("");
    }

    // Keep sales_type as the billing type (e.g. "credit") and
    // only switch the line item type for UI / item handling.
    const lineType = newType === "product" ? "product" : "service";

    // Clear existing items and add one fresh row
    setInvoiceItems([
      {
        type: lineType,
        ledger: "",
        description: "",
        qty: 1,
        price: 0,
        discountRate: "",
        discountAmount: "",
        discount: 0,
        vatRate: "Premium",
        vat: 0,
        vatChecked: true,
        tax_id: 1,
        wht: 0,
        total: 0,
        item_id: null // Fix: Explicitly set item_id
      }
    ]);
  };

  useEffect(() => {
    const fetchVatRates = async () => {
      const token = localStorage.getItem("authToken");
      try {
        const res = await api.get(`https://api-sme.promixaccounting.com/api/v1/taxes/company-tax`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        if (res?.data?.status && Array.isArray(res.data.data)) {
          setVatRates(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch VAT rates:", error);
      }
    };

    fetchVatRates();
  }, []);

  const handleDeleteRow = (index) => {
    const updated = [...invoiceItems];
    updated.splice(index, 1);
    setInvoiceItems(updated);
  };

  const clampPct = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return 0;
    return Math.max(0, Math.min(100, n));
  };

  const toNumber = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const recomputeRow = (row, changedField) => {
    const qty = toNumber(row.qty);
    const price = toNumber(row.price);
    const lineAmount = qty * price;

    let pct = row.discountRate;
    let amt = row.discountAmount;

    if (changedField === "discountRate") {
      const p = clampPct(pct);
      amt = lineAmount ? ((p / 100) * lineAmount) : 0;
      row.discountRate = pct === "" ? "" : p;
      row.discountAmount = pct === "" ? "" : +amt.toFixed(2);
    } else if (changedField === "discountAmount") {
      const a = Math.max(0, Math.min(lineAmount, toNumber(amt)));
      pct = lineAmount ? (a / lineAmount) * 100 : 0;
      row.discountAmount = amt === "" ? "" : +a.toFixed(2);
      row.discountRate = amt === "" ? "" : +pct.toFixed(2);
    } else {
      if (row.discountRate !== "" && lineAmount) {
        const p = clampPct(row.discountRate);
        row.discountAmount = +((p / 100) * lineAmount).toFixed(2);
        row.discountRate = p;
      } else if (row.discountAmount !== "" && lineAmount) {
        const a = Math.max(0, Math.min(lineAmount, toNumber(row.discountAmount)));
        row.discountRate = +((a / lineAmount) * 100).toFixed(2);
        row.discountAmount = +a.toFixed(2);
      } else {
        row.discountRate = row.discountRate === "" ? "" : 0;
        row.discountAmount = row.discountAmount === "" ? "" : 0;
      }
    }

    const discount = toNumber(row.discountAmount);
    const taxable = Math.max(0, lineAmount - discount);

    row.discount = discount;
    row.vat = row.tax_id === 1 ? 0.075 * taxable : 0;
    row.wht = row.wht_id === 2 ? 0.02 * taxable : 0;
    row.total = taxable + row.vat + row.wht;

    return row;
  };

  // Get current items based on selected type
  const getCurrentItems = () => {
    if (type === "product") { // Fixed to singular form
      return isSuperAdmin && selectedStoreId ? products : (products.length > 0 ? products : stocks);
    } else {
      // When on Services tab, use chart of accounts as the selectable list.
      return accounts;
    }
  };

  const handleChange = (index, field, value) => {
    setInvoiceItems(prev => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };

      // Track if user manually edited description so auto-fill won't overwrite it
      if (field === 'description') {
        row.userEditedDescription = true;
      }

      if (field === "ledger") {
        const currentItems = getCurrentItems();
        const selectedItem = Array.isArray(currentItems)
          ? currentItems.find(item => item.name === value)
          : undefined;

        if (selectedItem) {
          // Ensure price and quantity are always numbers
          row.price = Number(selectedItem.price) || 0;
          row.item_id = selectedItem.id;
          // Auto-fill description only when the user has not manually edited it.
          // If the user edited it, preserve their input.
          if (!row.userEditedDescription) {
            row.description = selectedItem.description || "";
          }
          row.available_quantity = Number(selectedItem.quantity) || 0;
        } else {
          row.price = 0;
          row.item_id = null;
          // When clearing a selection, clear the description only if the
          // user hasn't provided a custom description.
          if (!row.userEditedDescription) row.description = "";
          row.available_quantity = 0;
        }
      }

      if (field === "vatChecked") {
        row.vatChecked = Boolean(value);
        row.tax_id = value ? 1 : null;
      }
      if (field === "wthChecked") row.wht_id = value ? 2 : null;

      updated[index] = recomputeRow(row, field);

      // Immediate quantity check (no Swal, use error state)
      if (field === "qty") {
        const currentItems = getCurrentItems();
        const selectedItem = Array.isArray(currentItems)
          ? currentItems.find(i => (row.item_id ? i.id === row.item_id : i.name === row.ledger))
          : undefined;

        const available = selectedItem ? parseFloat(selectedItem.quantity || "0") : undefined;
        const requested = parseFloat(value);

        setQuantityErrors(prevErrors => {
          const errors = [...prevErrors];
          if (available !== undefined && !isNaN(requested) && requested > available) {
            errors[index] = `There is not enough product to meet up that quantity. Only ${available} available in stock.`;
          } else {
            errors[index] = "";
          }
          return errors;
        });
      }
      // Clear error if ledger changes
      if (field === "ledger") {
        setQuantityErrors(prevErrors => {
          const errors = [...prevErrors];
          errors[index] = "";
          return errors;
        });
      }
      return updated;
    });
  };

  const { subtotal, totalDiscount, totalVAT, totalWHT, grandTotal } = resolveInvoiceTotals(editInvoice, invoiceItems);

  const handleSaveInvoice = async () => {
    const token = localStorage.getItem("authToken");

    const hasCustomer =
      Boolean(String(selectedCustomerId || "").trim()) ||
      Boolean(String(selectedCustomerName || "").trim()) ||
      Boolean(String(customerInputValue || "").trim());

    if (!reference || !currency || !hasCustomer || invoiceItems.length === 0) {
      Swal.fire("Missing Fields", "Please fill all required fields.", "warning");
      return;
    }

    // Fix 5: Validate that all items have valid item_id
    const hasInvalidItems = invoiceItems.some(row => !row.item_id || !row.ledger.trim());
    if (hasInvalidItems) {
      Swal.fire("Invalid Items", "Please select valid products/services for all rows.", "warning");
      return;
    }

    const item_id = [];
    const quantity = [];
    const item_amount = [];
    const referencePerItem = [];
    const tax_id = [];
    const wht_id = [];
    const discount = [];
    const discount_percentage = [];

    let sub_total = 0;
    let total_discount = 0;
    let total_vat = 0;
    let total_wht = 0;

    invoiceItems.forEach((row) => {
      const qty = Number(row.qty) || 0;
      const price = Number(row.price) || 0;
      const lineAmount = qty * price;
      const discountAmt = Number(row.discount) || 0;
      const taxable = Math.max(0, lineAmount - discountAmt);

      const vatAmount = Number(row.vat) || 0;
      const whtAmount = Number(row.wht) || 0;

      // Fix 6: Ensure item_id is a valid number
      item_id.push(Number(row.item_id) || null);
      quantity.push(qty);
      item_amount.push(+lineAmount.toFixed(2));
      referencePerItem.push(String(row.description ?? "").trim());

      tax_id.push(row.tax_id ?? null);
      wht_id.push(row.wht_id ?? null);

      discount.push(discountAmt > 0 ? +discountAmt.toFixed(2) : null);
      const pct = row.discountRate === "" ? null : Number(row.discountRate);
      discount_percentage.push(pct && pct > 0 ? +pct.toFixed(2) : null);

      sub_total += taxable;
      total_discount += discountAmt;
      total_vat += vatAmount;
      total_wht += whtAmount;
    });

    const grand_total = sub_total + total_vat + total_wht;

    const lineDescriptions = invoiceItems
      .map(r => (r.description || "").trim())
      .filter(Boolean);

    // For services endpoint, API expects description to be an array.
    const descriptionField =
      type === "service"
        ? (lineDescriptions.length ? lineDescriptions : [])
        : (lineDescriptions.join("; ") || "");

    // Build payloads separately for product vs service endpoints,
    // because the service_offered API expects a simpler shape and
    // was throwing "Array to string conversion" errors when we sent
    // the full line-item arrays.
    let payload;
    const isUpdate = isEditMode && editInvoice?.id;

    if (type === "service") {
      // For services, mirror the product totals logic and also send
      // tax_id / wht_id and discount / discount_percentage so that
      // VAT, WHT and discounts selected in the UI are honoured.
      payload = {
        sales_type: "credit",
        invoice_number: reference,
        // Allow either an existing customer ID or a typed customer name.
        customer_id:
          selectedCustomerId ||
          String(selectedCustomerName || "").trim() ||
          String(customerInputValue || "").trim(),
        due_date: dueDate,
        invoice_date: transactionDate,
        currency: currency,
        // Service API expects description as an array
        description: Array.isArray(descriptionField)
          ? descriptionField
          : (descriptionField ? [descriptionField] : []),

        // Line-level arrays (aligned)
        quantity,
        item_amount,
        reference: referencePerItem,
        tax_id,
        wht_id,
        // Send selected chart-of-account IDs for each service row
        income_account_id: item_id,
        discount,
        discount_percentage,

        // Totals – keep them consistent like products
        sub_total: +sub_total.toFixed(2),
        total_discount: +total_discount.toFixed(2),
        total_vat: +total_vat.toFixed(2),
        total_wht: +total_wht.toFixed(2),
        amount: +grand_total.toFixed(2),
        total_price: +grand_total.toFixed(2),
      };
    } else {
      payload = {
        // Force sales_type to "credit" for products as well
        sales_type: "credit",
        invoice_number: reference,
        // Allow either an existing customer ID or a typed customer name.
        customer_id:
          selectedCustomerId ||
          String(selectedCustomerName || "").trim() ||
          String(customerInputValue || "").trim(),
        due_date: dueDate,
        invoice_date: transactionDate,
        currency: currency,
        description: descriptionField,

        // line arrays (aligned)
        item_id,
        quantity,
        item_amount,
        reference: referencePerItem,
        tax_id,
        wht_id,
        discount,
        discount_percentage,

        // totals
        sub_total: +sub_total.toFixed(2),
        total_discount: +total_discount.toFixed(2),
        total_vat: +total_vat.toFixed(2),
        total_wht: +total_wht.toFixed(2),
        amount: +grand_total.toFixed(2),
        total_price: +grand_total.toFixed(2),
        ...(isSuperAdmin && selectedStoreId ? { store_id: selectedStoreId } : {})
      };
    }

    console.log("payload:", payload);

    setIsSaving(true);
    let autoRetryDone = false;
    try {
      const endpoint = isUpdate
        ? `${BASE_URL}/v1/update_sales_invoice`
        : (type === "service"
            ? `${BASE_URL}/v1/service_offered`
            : `${BASE_URL}/v1/sales_to_customer`);

      const res = await api.post(endpoint, withCompanyId(payload), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const createdInvoice = res?.data?.data;
      setResponseData(createdInvoice);
      
      Swal.fire(
        "Success",
        isUpdate
          ? "Invoice updated successfully!"
          : (type === "service"
              ? "Service entry saved successfully!"
              : "Invoice saved successfully!"),
        "success"
      ).then(() => {
        // Auto-navigate to invoice report after success.
        // Enrich the created invoice with customer details so BILL TO shows name/address.
        if (createdInvoice) {
          let billTo = null;

          // Try to match by selected customer id from the creatable select
          if (selectedCustomerId) {
            const matchById = customers.find(
              (c) => String(c?.id) === String(selectedCustomerId)
            );
            if (matchById) {
              billTo = {
                id: matchById.id,
                name:
                  matchById.name ||
                  matchById.customer_name ||
                  matchById.business_name ||
                  "",
                address: matchById.address || "",
                email: matchById.email || "",
                phone_number: matchById.phone_number || matchById.phone || "",
              };
            }
          }

          // Fallback: use the selected customer name or typed value if we don't have an id match
          if (!billTo) {
            const name =
              selectedCustomerName ||
              String(customerInputValue || "").trim();
            if (name) {
              billTo = {
                id: createdInvoice.customer_id ?? selectedCustomerId ?? null,
                name,
                address: "",
                email: "",
                phone_number: "",
              };
            }
          }

          const invoiceForPreview = {
            ...createdInvoice,
            customer:
              billTo ||
              createdInvoice.customer ||
              createdInvoice.supplier ||
              null,
          };

          navigate("/reportinvoice", {
            state: {
              invoice: invoiceForPreview,
              invoiceType: type === "service" ? "Sales Invoice" : "INVOICE",
            },
          });
        }
      });
      
      setShowPrint(true);

      // Reset form data and fetch fresh data
      resetFormData();
      await fetchInitialData();

    } catch (error) {
      const serverData = error?.response?.data || {};

      // If backend returned corrected totals, apply them and retry once
      if (!autoRetryDone && serverData?.data?.correct_values) {
        try {
          const cv = serverData.data.correct_values;
          console.warn('Backend suggested corrected totals:', cv);

          // Apply corrected totals (ensure numeric types)
          payload = {
            ...payload,
            sub_total: Number(cv.sub_total || payload.sub_total || 0),
            total_discount: Number(cv.total_discount || payload.total_discount || 0),
            total_vat: Number(cv.total_vat || payload.total_vat || 0),
            total_wht: Number(cv.total_wht || payload.total_wht || 0),
            total_price: Number(cv.total_price || payload.total_price || 0),
            amount: Number(cv.amount || payload.amount || 0),
          };

          autoRetryDone = true;
          const endpoint = isUpdate
            ? `${BASE_URL}/v1/update_sales_invoice`
            : (type === "service"
                ? `${BASE_URL}/v1/service_offered`
                : `${BASE_URL}/v1/sales_to_customer`);

          const retryRes = await api.post(endpoint, withCompanyId(payload), {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          const createdInvoice = retryRes?.data?.data;
          setResponseData(createdInvoice);
          
          Swal.fire(
            "Success",
            isUpdate
              ? "Invoice updated successfully!"
              : (type === "service"
                  ? "Service entry saved successfully!"
                  : "Invoice saved successfully!"),
            "success"
          ).then(() => {
            // Auto-navigate to invoice report after success
            if (createdInvoice) {
              navigate("/reportinvoice", {
                state: {
                  invoice: createdInvoice,
                  invoiceType: type === "service" ? "SERVICE INVOICE" : "INVOICE",
                },
              });
            }
          });
          
          setShowPrint(true);
          resetFormData();
          await fetchInitialData();
          setIsSaving(false);
          return;
        } catch (retryErr) {
          console.error('Retry with corrected totals failed:', retryErr?.response || retryErr.message || retryErr);
          // fall through to show original error
        }
      }

      const msg = serverData?.message || "Submission failed!";
      Swal.fire("Failed", JSON.stringify(msg), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const totalVat = invoiceItems.reduce((sum, item) => sum + (item.vat || 0), 0);
  const totalWht = invoiceItems.reduce((sum, item) => sum + (item.wht || 0), 0);
  const totalAmount = invoiceItems.reduce((sum, item) => sum + (item.total || 0), 0);

  const handlePrintReceipt = () => {
    navigate("/printreceipts", {
      state: {
        responseData
      },
    });
  };

  const handlePOSReceipt = () => {
    navigate("/pos_receipt", {
      state: {
        result: responseData,
        mode: "pos",
      },
    });
  };

  // Format currency options for React Select
  const currencyOptions = currencies.map(cur => ({
    value: cur.id,
    label: `${cur.symbol} ${cur.code} - ${cur.country}`,
    currency: cur
  }));

  console.log(responseData);

  return (
    <div>
      <HeaderNav activePage={pageMap[location.pathname]} />
      <div className={classes.Container}>
        <header className={classes.Header}>
          <h2 className={classes.Title}>{isEditMode ? 'Edit Sales Invoice' : 'New Sales Invoices'}</h2>
        </header>
        <form className={classes.form}>
          {isSuperAdmin && type !== "service" && (
            <div className={classes.group1}>
              <label>Store</label>
              <Select
                isClearable
                isSearchable
                isDisabled={loadingStores}
                placeholder={loadingStores ? 'Loading stores...' : 'Select store'}
                options={stores.map((store) => ({
                  value: String(store.id),
                  label: store.name || `Store ${store.id}`
                }))}
                value={(() => {
                  const options = stores.map((store) => ({
                    value: String(store.id),
                    label: store.name || `Store ${store.id}`
                  }));
                  return options.find(o => o.value === String(selectedStoreId)) || null;
                })()}
                onChange={(option) => setSelectedStoreId(option?.value || "")}
              />
            </div>
          )}

          <div className={classes.group}>
            <label>Customer Name*</label>
            <CreatableSelect
              isClearable
              isSearchable
              isLoading={loadingCustomers}
              inputValue={customerInputValue}
              onInputChange={(value, actionMeta) => {
                if (actionMeta?.action === "input-change") {
                  setCustomerInputValue(value);
                  // If a customer was previously selected and the user starts typing,
                  // treat it as a new/different customer entry.
                  if (
                    Boolean(String(selectedCustomerId || "").trim()) ||
                    Boolean(String(selectedCustomerName || "").trim())
                  ) {
                    setSelectedCustomerId("");
                    setSelectedCustomerName("");
                  }
                }
                return value;
              }}
              placeholder={loadingCustomers ? "Loading customers..." : "Search or type customer"}
              options={[{ value: '__add__', label: 'Add Customer' }].concat(
                customers
                  .filter((cust) => cust && (cust.name || cust.customer_name || cust.business_name))
                  .map((cust) => ({
                    value: cust.id,
                    label: cust.name || cust.customer_name || cust.business_name,
                    raw: cust,
                  }))
              )}
              value={(() => {
                if (!selectedCustomerId && !selectedCustomerName) return null;
                const id = String(selectedCustomerId || "").trim();
                const name = String(selectedCustomerName || "").trim();

                const matchById = id
                  ? customers.find((c) => String(c?.id) === id)
                  : null;
                if (matchById) {
                  return {
                    value: matchById.id,
                    label: matchById.name || matchById.customer_name || matchById.business_name,
                    raw: matchById,
                  };
                }

                const matchByName = name
                  ? customers.find(
                    (c) =>
                      String(c?.name || c?.customer_name || c?.business_name || "")
                        .toLowerCase()
                        .trim() === name.toLowerCase()
                  )
                  : null;
                if (matchByName) {
                  return {
                    value: matchByName.id,
                    label: matchByName.name || matchByName.customer_name || matchByName.business_name,
                    raw: matchByName,
                  };
                }

                // Typed customer name that isn't in fetched data.
                return name ? { value: `__new__:${name}`, label: name, __isNew__: true } : null;
              })()}
              onChange={(selected) => {
                if (!selected) {
                  setSelectedCustomerName("");
                  setSelectedCustomerId("");
                  setCustomerInputValue("");
                  return;
                }

                // If user clicked the explicit "Add Customer" option
                if (selected.value === '__add__') {
                  const typed = String(customerInputValue || "").trim();
                  setNewCustomerInitialName(typed || "");
                  setIsAddCustomerModalOpen(true);
                  setSelectedCustomerName("");
                  setSelectedCustomerId("");
                  setCustomerInputValue("");
                  return;
                }

                if (selected.raw) {
                  setSelectedCustomerName(
                    selected.raw.name || selected.raw.customer_name || selected.raw.business_name || ""
                  );
                  setSelectedCustomerId(selected.raw.id);
                  setCustomerInputValue("");
                  return;
                }

                // Fallback: best-effort mapping
                setSelectedCustomerName(String(selected.label || "").trim());
                setSelectedCustomerId(String(selected.value || "").trim());
                setCustomerInputValue("");
              }}
              onCreateOption={(inputValue) => {
                const typed = String(inputValue || "").trim();
                if (!typed) return;

                // If it actually matches an existing customer, keep using the ID.
                const match = customers.find(
                  (c) =>
                    String(c?.name || c?.customer_name || c?.business_name || "")
                      .toLowerCase()
                      .trim() === typed.toLowerCase()
                );

                if (match) {
                  setSelectedCustomerName(match.name || match.customer_name || match.business_name || typed);
                  setSelectedCustomerId(match.id);
                  setCustomerInputValue("");
                } else {
                  // Open modal for new customer
                  setNewCustomerInitialName(typed);
                  setIsAddCustomerModalOpen(true);
                }
              }}
              formatCreateLabel={(inputValue) => `Add Customer "${inputValue}"`}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              loadingMessage={() => "Loading customers..."}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? `No match. Select "Add Customer" below to create.`
                  : "No customers found"
              }
            />
          </div>

          <div className={classes.group1}>
            <label>Invoice Date</label>
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              required
            />
          </div>

          <div className={classes.group1}>
            <label>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
            />
          </div>

          <div className={classes.group2}>
            <label>Reference</label>
            <input type="text" value={reference} readOnly />
          </div>

          <div className={classes.group1}>
            <label>Currency*</label>
            <Select
              isClearable
              isSearchable
              placeholder="Select currency"
              options={currencyOptions}
              value={selectedCurrency}
              onChange={(option) => {
                setSelectedCurrency(option);
                setCurrency(option ? option.value : "");
              }}
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              required
            />
          </div>

          {/* Sales Type is fixed to 'credit' and hidden from users */}

        </form>

        <div className={classes.invoicecont}>
          <div style={{ marginBottom: "1rem" }}>
            <button
              type="button"
              className={type === "product" ? classes.activeTab : classes.inactiveTab}
              onClick={() => handleTypeSwitch("product")}
            >
              Products/Services
            </button>
            {/* <button
              type="button"
              className={type === "service" ? classes.activeTab : classes.inactiveTab}
              onClick={() => handleTypeSwitch("service")}
            >
              Services
            </button> */}
          </div>
          <table className={classes.invoiceTable}>
            <thead>
              <tr>
                <th>{type === "product" ? "Products/Services" : "Accounts"}</th>
                <th>Description</th>
                <th>{type === "product" ? "Qty" : "Qty*"}</th>
                <th>{type === "product" ? "Price/Rate" : "Rate*"}</th>
                <th>Discount(%)</th>
                <th>Discount Amount</th>
                <th>VAT</th>
                <th>WHT</th>
                <th>Total</th>
                <th><button type="button" className={classes.addRowBtn} onClick={handleAddRow}>+</button></th>
              </tr>
            </thead>
            <tbody>
              {invoiceItems.map((item, idx) => {
                const currentItems = getCurrentItems();
                const itemOptions = Array.isArray(currentItems)
                  ? currentItems.map((product) => ({
                    value: product.id,
                    label: product.name,
                    raw: product,
                  }))
                  : [];
                const selectedItemOption =
                  itemOptions.find((option) => String(option.value) === String(item.item_id)) ||
                  itemOptions.find((option) => option.label === item.ledger);
                const options = selectedItemOption || !item.ledger
                  ? itemOptions
                  : [
                    ...itemOptions,
                    {
                      value: item.item_id ?? item.ledger,
                      label: item.ledger,
                      raw: {
                        id: item.item_id,
                        name: item.ledger,
                        price: item.price,
                        description: item.description,
                      },
                    },
                  ];
                const listId = `itemList-${idx}`;
                const isProductTab = type === "product";
                const isProductFieldDisabled = isSuperAdmin && !selectedStoreId && isProductTab;

                return (
                  <tr key={idx}>
                    <td className={classes.productCell}>
                      <div style={{ position: "relative" }}>
                        <Select
                          inputId={listId}
                          isClearable
                          isSearchable
                          isDisabled={isProductFieldDisabled}
                          placeholder={`Search or type ${type === "product" ? "product/service" : "Account"}`}
                          options={options}
                          value={selectedItemOption || options.find((option) => option.label === item.ledger) || null}
                          onChange={(selected) => {
                            if (selected && selected.raw) {
                              const selectedItem = selected.raw;
                              handleChange(idx, "ledger", selectedItem.name);
                              handleChange(idx, "price", selectedItem.price);
                              handleChange(idx, "item_id", selectedItem.id);
                              handleChange(
                                idx,
                                "available_quantity",
                                parseFloat(selectedItem.quantity || "")
                              );
                            } else {
                              // Cleared selection
                              handleChange(idx, "ledger", "");
                              handleChange(idx, "price", 0);
                              handleChange(idx, "item_id", null);
                              handleChange(idx, "available_quantity", undefined);
                            }
                          }}
                          styles={{
                            container: (base) => ({
                              ...base,
                              minWidth: 180,
                            }),
                            control: (base, state) => ({
                              ...base,
                              minHeight: 30,
                              height: 30,
                              paddingTop: 0,
                              paddingBottom: 0,
                              borderRadius: 4,
                              boxShadow: state.isFocused ? base.boxShadow : "none",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              paddingTop: 0,
                              paddingBottom: 0,
                              minHeight: 30,
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: 30,
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          menuPortalTarget={document.body}
                        />
                        {/* Show available quantity for selected product below input */}
                        {isProductTab && item.ledger && typeof item.available_quantity !== "undefined" && (
                          <div
                            style={{
                              fontSize: '0.8em',
                              color: '#888',
                              borderRadius: '4px',
                              // boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                          >
                            Available: {Number(item.available_quantity) || 0}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className={classes.descriptionCell}>
                      {focusedDescriptionIndex === idx ? (
                        <textarea
                          autoFocus
                          value={item.description}
                          onChange={(e) => handleChange(idx, 'description', e.target.value)}
                          rows={3}
                          onBlur={() => setFocusedDescriptionIndex(null)}
                        />
                      ) : (
                        <input
                          type="text"
                          value={item.description}
                          onFocus={() => setFocusedDescriptionIndex(idx)}
                          onChange={(e) => handleChange(idx, 'description', e.target.value)}
                        />
                      )}
                    </td>

                    <td style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => handleChange(idx, "qty", e.target.value)}
                        placeholder={type === "product" ? "Qty*" : "Hrs*"}
                        required
                      />
                      {/* Show quantity error below input */}
                      {quantityErrors[idx] && (
                        <div style={{
                          position: 'absolute',
                          // left: '100%',
                          // top: '50%',
                          transform: 'translateY(-50%)',
                          background: '#ffeaea',
                          color: '#d32f2f',
                          fontSize: '0.85em',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          whiteSpace: 'nowrap',
                          zIndex: 10,
                          // marginLeft: '8px'
                        }}>
                          {quantityErrors[idx]}
                        </div>
                      )}
                    </td>

                    <td>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => handleChange(idx, "price", e.target.value)}
                        placeholder={type === "product" ? "Price*" : "Rate*"}
                        required
                        className={classes.totalInput}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={item.discountRate === "" ? "" : item.discountRate}
                        onChange={(e) => handleChange(idx, "discountRate", e.target.value)}
                        placeholder="%"
                        className={classes.discountInput}
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.discountAmount === "" ? "" : item.discountAmount}
                        onChange={(e) => handleChange(idx, "discountAmount", e.target.value)}
                        placeholder="0.00"
                        className={classes.totalInput}
                      />
                    </td>

                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(item.vatChecked)}
                        disabled={shouldDisableVatCheckbox(autoVatDeduction)}
                        onChange={(e) => handleChange(idx, 'vatChecked', e.target.checked)}
                      />
                    </td>

                    <td>
                      <input
                        type="checkbox"
                        checked={item.wthChecked || false}
                        onChange={(e) => handleChange(idx, 'wthChecked', e.target.checked)}
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        value={Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        readOnly
                        className={classes.totalInput}
                      />
                    </td>


                    <td><Trash2 size={16} onClick={() => handleDeleteRow(idx)} className={classes.deleteIcon} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className={classes.paginationContainer}>
            <div className={classes.paginationControls}>
              <button>First</button>
              <button className={classes.active}>1</button>
              <button>2</button>
              <button>Last</button>
              <span className={classes.pageText}>Displaying 1 - {invoiceItems.length} of {invoiceItems.length}</span>
            </div>
          </div>

          <div className={classes.foot}>
            <div className={classes.Footer}>
              <div className={`${classes.footerSection} ${classes.comments}`}>
                <label>Comments</label>
                <textarea className={classes.commentsBox}></textarea>
              </div>
              <div className={`${classes.footerSection} ${classes.policies}`}>
                <label>Policies and guidelines</label>
                <textarea className={classes.policiesBox}></textarea>
              </div>
              <div className={`${classes.footerSection} ${classes.summary}`}>
                <div className={classes.shipping}>
                  <label>Shipping</label>
                  <select><option>Standard (UK)</option></select>
                  <input type="text" defaultValue="0.00" readOnly />
                </div>
                <div className={classes.totals}>
                  <div className={classes.totalRow}>
                    <span>Subtotal:</span>
                    <span>
                      {(() => {
                        const selected = currencies.find(c => c.id === currency);
                        return selected ? `${selected.symbol} ` : "";
                      })()}
                      {subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className={classes.totalRow}>
                    <span>W.H.T:</span>
                    <span>
                      {(() => {
                        const selected = currencies.find(c => c.id === currency);
                        return selected ? `${selected.symbol} ` : "";
                      })()}
                      {totalWHT.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className={classes.totalRow}>
                    <span>VAT (7.5%):</span>
                    <span>
                      {(() => {
                        const selected = currencies.find(c => c.id === currency);
                        return selected ? `${selected.symbol} ` : "";
                      })()}
                      {totalVAT.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className={classes.totalRow}>
                    <span>Total Discount:</span>
                    <span>
                      {(() => {
                        const selected = currencies.find(c => c.id === currency);
                        return selected ? `${selected.symbol} ` : "";
                      })()}
                      {totalDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className={`${classes.totalRow} ${classes.total}`}>
                    <span>Total:</span>
                    <strong>
                      {(() => {
                        const selected = currencies.find(c => c.id === currency);
                        return selected ? `${selected.symbol} ` : "";
                      })()}
                      {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </strong>
                  </div>

                </div>
                <div className={classes.actionButtons}>
                  <button className={classes.cancelButton}>Cancel</button>
                  <button
                    className={classes.saveButton}
                    type="button"
                    onClick={handleSaveInvoice}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : (
                      isEditMode ? "Update" : "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showPrint && responseData && (
          <div className={classes.printButtons}>
            <button onClick={handlePrintReceipt} className={classes.printBtn}>
              Print Receipt
            </button>
            <button onClick={handlePOSReceipt} className={classes.printBtn}>
              POS Receipts
            </button>
          </div>
        )}

      </div>
      <Footer />

      <AddCustomerModal
        isOpen={isAddCustomerModalOpen}
        onClose={() => setIsAddCustomerModalOpen(false)}
        onSuccess={handleCustomerAdded}
        initialName={newCustomerInitialName}
      />

    </div>
  );
};

export default ActionPage;